// Casey email-draft chat: stream a reply, refine, insert.
//
// State machine per drafting cycle:
//   idle → streaming → ready (Casey done) → idle
// History is preserved across refinement turns so Casey can iterate
// ("make it shorter", "noem ook de deadline", etc.) coherently.

import { useCallback, useState } from "react";
import { streamDraftReply, type DraftLength, type DraftMessage, type DraftTone } from "../../lib/draft";
import type { CandidateCase, SearchResultCase } from "../../lib/cases";
import type { MailItemSummary } from "../../lib/office";
import { getBodyText, insertReplyBody } from "../../lib/office";
import { finalize, type FinalizeResponse } from "../../lib/finalize";

interface ChatPanelProps {
  caseRef: CandidateCase | SearchResultCase;
  mailItem: MailItemSummary;
}

interface ChatState {
  history: DraftMessage[];      // prior turns (alternating user/assistant)
  current: string;              // streaming buffer for the in-progress draft
  status: "idle" | "streaming" | "ready" | "error";
  toolStatus: string | null;    // most recent tool name (UI status line)
  error: string | null;
}

const initialState: ChatState = { history: [], current: "", status: "idle", toolStatus: null, error: null };

const TONES: { value: DraftTone; label: string }[] = [
  { value: "formal", label: "Formeel" },
  { value: "neutral", label: "Neutraal" },
  { value: "informal", label: "Informeel" },
];

const LENGTHS: { value: DraftLength; label: string }[] = [
  { value: "short", label: "Kort" },
  { value: "medium", label: "Middel" },
  { value: "long", label: "Lang" },
];

const QUICK_ACTIONS: { label: string; instruction: string }[] = [
  { label: "Korter", instruction: "Maak de reply duidelijk korter — neem alleen de essentie op." },
  { label: "Formeler", instruction: "Herschrijf in een formelere, juridisch-zakelijke toon." },
  { label: "Voeg deadline toe", instruction: "Voeg een passende reactie- of actiedeadline toe." },
];

export function ChatPanel({ caseRef, mailItem }: ChatPanelProps) {
  const [state, setState] = useState<ChatState>(initialState);
  const [tone, setTone] = useState<DraftTone>("neutral");
  const [length, setLength] = useState<DraftLength>("medium");
  const [refineInput, setRefineInput] = useState("");
  const [conflict, setConflict] = useState<{ preview: string } | null>(null);
  const [finalizeResult, setFinalizeResult] = useState<FinalizeResponse | null>(null);

  const runDraft = useCallback(async (instruction?: string) => {
    setState((s) => ({ ...s, status: "streaming", current: "", toolStatus: null, error: null }));

    let bodyText = "";
    try {
      bodyText = await getBodyText();
    } catch {
      // body fetch can fail in some Compose states — we still proceed with empty body
    }

    const ac = new AbortController();
    let buffer = "";
    let final = "";

    try {
      for await (const chunk of streamDraftReply(
        {
          caseId: caseRef.caseId,
          email: {
            fromEmail: mailItem.from,
            fromDisplayName: mailItem.fromDisplayName || undefined,
            subject: mailItem.subject,
            bodyText,
          },
          history: state.history,
          instruction,
          tone,
          length,
        },
        ac.signal,
      )) {
        if (chunk.type === "text") {
          buffer += chunk.delta;
          setState((s) => ({ ...s, current: buffer }));
        } else if (chunk.type === "tool_call") {
          setState((s) => ({ ...s, toolStatus: `Casey raadpleegt het dossier (${chunk.name})…` }));
        } else if (chunk.type === "tool_result") {
          setState((s) => ({ ...s, toolStatus: null }));
        } else if (chunk.type === "done") {
          final = chunk.finalText || buffer;
          setState((s) => ({
            ...s,
            current: final,
            status: "ready",
            toolStatus: null,
            history: [
              ...s.history,
              ...(instruction ? [{ role: "user" as const, content: instruction }] : []),
              { role: "assistant", content: final },
            ],
          }));
          return;
        } else if (chunk.type === "error") {
          setState((s) => ({ ...s, status: "error", error: chunk.message, toolStatus: null }));
          return;
        }
      }
      // Stream ended without 'done' — treat what we have as final
      setState((s) => ({ ...s, status: "ready", current: buffer, toolStatus: null }));
    } catch (e) {
      if ((e as { name?: string }).name === "AbortError") return;
      setState((s) => ({
        ...s,
        status: "error",
        error: e instanceof Error ? e.message : "Casey-fout",
        toolStatus: null,
      }));
    }
  }, [caseRef.caseId, mailItem, state.history, tone, length]);

  const handleInsert = useCallback(async (force: boolean) => {
    setConflict(null);
    setFinalizeResult(null);
    try {
      const r = await insertReplyBody(state.current, { force });
      if (r.conflict) {
        setConflict({ preview: r.conflictPreview ?? "" });
        return;
      }
      // Successful insert: clear current draft (history kept for context)
      setState((s) => ({ ...s, current: "", status: "idle" }));

      // Best-effort finalize. Only meaningful when we have a source-message
      // ID — Read mode does, fresh Compose without an in-reply-to does not.
      if (mailItem.itemId) {
        try {
          const fin = await finalize({
            caseId: caseRef.caseId,
            sourceMessageId: mailItem.itemId,
          });
          setFinalizeResult(fin);
        } catch (finErr) {
          // Don't undo the insert on finalize failure — the user already
          // has their reply pre-filled; archiving is best-effort.
          setFinalizeResult({
            moved: false,
            warning: finErr instanceof Error ? finErr.message : "Archiveren mislukt",
          });
        }
      }
    } catch (e) {
      setState((s) => ({ ...s, error: e instanceof Error ? e.message : "Invoegen mislukt" }));
    }
  }, [state.current, mailItem.itemId, caseRef.caseId]);

  const handleRefine = useCallback(() => {
    const instr = refineInput.trim();
    if (!instr || state.status === "streaming") return;
    setRefineInput("");
    void runDraft(instr);
  }, [refineInput, state.status, runDraft]);

  const showInitial = state.status === "idle" && state.history.length === 0;

  return (
    <section className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-chrome-subtle">Casey reply</div>

      {showInitial && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Selector label="Toon" value={tone} options={TONES} onChange={setTone} />
            <Selector label="Lengte" value={length} options={LENGTHS} onChange={setLength} />
          </div>
          <button
            type="button"
            onClick={() => runDraft()}
            className="w-full px-4 py-2 bg-accent hover:bg-accent-hover text-white text-sm font-medium rounded transition-colors"
          >
            Genereer reply met Casey
          </button>
        </div>
      )}

      {(state.status === "streaming" || state.status === "ready" || state.history.length > 0) && (
        <div className="rounded border border-chrome-border bg-white">
          <div className="px-3 py-2 max-h-72 overflow-y-auto">
            {state.current ? (
              <pre className="whitespace-pre-wrap font-sans text-sm text-chrome-text leading-relaxed">{state.current}</pre>
            ) : (
              <p className="text-xs text-chrome-subtle">Casey schrijft…</p>
            )}
          </div>
          {state.toolStatus && (
            <div className="px-3 py-1.5 border-t border-chrome-border text-xs text-chrome-subtle italic">
              {state.toolStatus}
            </div>
          )}
        </div>
      )}

      {state.status === "ready" && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_ACTIONS.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => runDraft(a.instruction)}
                className="px-2 py-1 text-xs rounded border border-chrome-border bg-white hover:border-accent hover:text-accent transition-colors"
              >
                {a.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={refineInput}
              onChange={(e) => setRefineInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleRefine();
                }
              }}
              placeholder="Verfijn (bv. 'noem ook 12 mei')"
              className="flex-1 px-3 py-1.5 text-sm rounded border border-chrome-border focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            />
            <button
              type="button"
              onClick={handleRefine}
              disabled={!refineInput.trim()}
              className="px-3 py-1.5 text-sm rounded border border-chrome-border hover:border-accent hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Verfijn
            </button>
          </div>
          <button
            type="button"
            onClick={() => handleInsert(false)}
            className="w-full px-4 py-2 bg-accent hover:bg-accent-hover text-white text-sm font-medium rounded transition-colors"
          >
            Voeg in reply in
          </button>
        </>
      )}

      {state.status === "error" && state.error && (
        <p className="text-xs text-red-600">{state.error}</p>
      )}

      {finalizeResult && (
        finalizeResult.moved ? (
          <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
            ✓ Mail gearchiveerd in dossier-folder.
          </div>
        ) : (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
            {finalizeResult.warning ?? "Mail niet gearchiveerd."}
          </div>
        )
      )}

      {conflict && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 space-y-2">
          <p className="text-xs text-amber-900">
            Het reply-venster bevat al inhoud. Vervangen?
          </p>
          <p className="text-xs text-chrome-subtle truncate" title={conflict.preview}>
            "{conflict.preview}…"
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleInsert(true)}
              className="flex-1 px-3 py-1.5 text-xs rounded bg-amber-600 hover:bg-amber-700 text-white"
            >
              Vervang
            </button>
            <button
              type="button"
              onClick={() => setConflict(null)}
              className="flex-1 px-3 py-1.5 text-xs rounded border border-chrome-border bg-white hover:border-chrome-text"
            >
              Annuleer
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

interface SelectorProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}

function Selector<T extends string>({ label, value, options, onChange }: SelectorProps<T>) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wide text-chrome-subtle mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="w-full px-2 py-1.5 text-sm rounded border border-chrome-border bg-white focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
